import React from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import theme from './theme';
import PageLayout from './components/PageLayout';
import PageHeader from './components/PageHeader';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PersonaList from './pages/PersonaList';
import PersonaSelection from './pages/PersonaSelection';
import Simulation from './pages/Simulation';
import Account from './pages/Account';
import MyPatients from './pages/MyPatients';
import MyFiles from './pages/MyFiles';
import LoadingSpinner from './components/LoadingSpinner';
import MaintenanceBlockHost from './components/MaintenanceBlockHost';
import PasswordOnboardingGate from './components/PasswordOnboardingGate';
import { buildLoginPathWithRedirect, getPostLoginRedirectPath } from './utils/authRedirect';
import { userHasMyHospitalModule } from './utils/moduleAccess';
import {
  SIMULATION_SETUP_LEGACY_PATH,
  SIMULATION_SETUP_PATH,
} from './constants/clientRoutes';

function RequireMyHospital({ children }) {
  const { user, isGuest } = useAuth();
  const location = useLocation();
  if (!user || isGuest) {
    return <Navigate to={buildLoginPathWithRedirect(location)} replace />;
  }
  if (!userHasMyHospitalModule(user)) {
    return (
      <PageLayout>
        <PageHeader
          title="My Hospital"
          subtitle="My Patients and My Files are part of the My Hospital module."
          showBackButton
          backPath={SIMULATION_SETUP_PATH}
        />
        <Typography color="text.secondary">
          This module is not enabled for your account.
        </Typography>
      </PageLayout>
    );
  }
  return children;
}

/** Allow magic-link reset while logged in; otherwise authenticated users are sent to the app. */
function ClientResetPasswordRoute() {
  const { user, isGuest } = useAuth();
  const [searchParams] = useSearchParams();
  const hasResetToken = Boolean(searchParams.get('token'));
  if (user && !isGuest && !hasResetToken) {
    return <Navigate to={SIMULATION_SETUP_PATH} replace />;
  }
  return <ResetPassword />;
}

function App() {
  const { loading, isAuthenticated, isGuest, user, refreshUser } = useAuth();
  const location = useLocation();
  const loginPathWithRedirect = buildLoginPathWithRedirect(location);
  const defaultAuthenticatedPath = SIMULATION_SETUP_PATH;
  const postLoginPath = getPostLoginRedirectPath(location.search, defaultAuthenticatedPath);

  if (!loading && user && !isGuest && user.mustCompletePasswordOnboarding) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenanceBlockHost />
        <PasswordOnboardingGate refreshUser={refreshUser} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MaintenanceBlockHost />
      {loading ? (
        <LoadingSpinner />
      ) : (
      <Box 
        className="App"
        sx={{
          minHeight: '100vh',
          width: '100%',
          // Ensure no margin reset from browser defaults
          m: 0,
          p: 0
        }}
      >
        <Routes>
          <Route 
            path="/login" 
            element={(user && !isGuest && !user.mustCompletePasswordOnboarding)
              ? <Navigate to={postLoginPath} replace />
              : <Login />} 
          />
          <Route 
            path="/register" 
            element={(user && !isGuest) ? <Navigate to={SIMULATION_SETUP_PATH} /> : <Register />} 
          />
          <Route 
            path="/forgot-password" 
            element={(user && !isGuest) ? <Navigate to={SIMULATION_SETUP_PATH} /> : <ForgotPassword />} 
          />
          <Route path="/reset-password" element={<ClientResetPasswordRoute />} />
          <Route 
            path={SIMULATION_SETUP_PATH}
            element={isAuthenticated ? <PersonaSelection /> : <Navigate to={loginPathWithRedirect} replace />} 
          />
          <Route 
            path={SIMULATION_SETUP_LEGACY_PATH}
            element={isAuthenticated ? <PersonaList /> : <Navigate to={loginPathWithRedirect} replace />} 
          />
          <Route path="/personas" element={<Navigate to={SIMULATION_SETUP_PATH} replace />} />
          <Route path="/personas/legacy" element={<Navigate to={SIMULATION_SETUP_LEGACY_PATH} replace />} />
          <Route
            path="/account"
            element={
              user && !isGuest
                ? <Account />
                : <Navigate to={loginPathWithRedirect} replace />
            }
          />
          <Route
            path="/my-patients/*"
            element={(
              <RequireMyHospital>
                <MyPatients />
              </RequireMyHospital>
            )}
          />
          <Route
            path="/my-files/*"
            element={(
              <RequireMyHospital>
                <MyFiles />
              </RequireMyHospital>
            )}
          />
          <Route 
            path="/simulation/:sessionId" 
            element={<Simulation />} 
          />
          <Route 
            path="/simulation" 
            element={<Navigate to={SIMULATION_SETUP_PATH} />} 
          />
          <Route 
            path="/resimulation" 
            element={<Navigate to={isAuthenticated ? SIMULATION_SETUP_PATH : "/login"} replace />} 
          />
          <Route path="/history" element={<Navigate to={SIMULATION_SETUP_PATH} replace />} />
          <Route 
            path="/" 
            element={<Navigate to={isAuthenticated ? SIMULATION_SETUP_PATH : "/login"} />} 
          />
        </Routes>
      </Box>
      )}
    </ThemeProvider>
  );
}

export default App;
